import React, { useState, useEffect } from 'react';
import { Users, Plus, UserPlus, CheckCircle, XCircle, Trash2, Edit3, Target, Crown } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal, LabeledInput } from '../../components/SharedUI';
import { getTmTeams, createTmTeam, updateTmTeam, deleteTmTeam, getTmTeamMembers, requestTmMember, approveTmMember, rejectTmMember, removeTmMember, formatSeconds, getTmPaymentSettings, saveTmPaymentSetting, createTmTeamPayment, createTmDeduction, toggleMemberFinancials } from '../../utils/tm_api';
import { getEmployees } from '../../utils/api';
import { parseArabicToEnglish } from '../../utils/formatters';

export default function TmTeamsPage() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = ['owner', 'admin'].includes(user.role);
  const isTeamLeader = user.role === 'team_leader';

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [showMembers, setShowMembers] = useState(null);
  const [confirmDeleteTeam, setConfirmDeleteTeam] = useState(null);
  const [employees, setEmployees] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      setTeams(await getTmTeams());
    } catch (e) { toast.error('خطأ في تحميل الفرق'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); getEmployees().then(setEmployees).catch(() => {}); }, []);

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--text-dark-h)]">إدارة الفرق</h1>
            <p className="text-sm text-[var(--text-muted)]">{teams.length} فريق</p>
          </div>
        </div>
        {isOwner && (
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all font-bold text-sm">
            <Plus className="w-4 h-4" /> فريق جديد
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-[var(--text-muted)]">جاري التحميل...</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {teams.map(team => (
            <div key={team.id} className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 hover:shadow-lg transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-blue-500/20 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[var(--text-dark-h)] flex items-center gap-2">
                      {team.name}
                      {(team.team_type === 'internal' || !team.leader_employee_id) && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 font-bold">داخلي</span>
                      )}
                    </h3>
                    <p className="text-xs text-[var(--text-muted)]">
                      {team.leader_name ? `القائد: ${team.leader_name}` : 'بدون قائد — مباشر مع المتابع'}
                    </p>
                  </div>
                </div>
                {isOwner && (
                  <div className="flex gap-1">
                    <button onClick={() => setEditTeam(team)} className="p-1.5 rounded-lg hover:bg-[var(--bg-flat-100)] transition">
                      <Edit3 className="w-4 h-4 text-[var(--text-gray)]" />
                    </button>
                    {confirmDeleteTeam === team.id ? (
                      <div className="flex gap-1 items-center bg-red-50 dark:bg-red-900/20 px-2 rounded-lg">
                        <span className="text-xs text-red-600 font-bold">متأكد؟</span>
                        <button onClick={async () => { await deleteTmTeam(team.id); setConfirmDeleteTeam(null); load(); }} className="p-1 hover:text-red-700 text-red-500"><Trash2 className="w-4 h-4" /></button>
                        <button onClick={() => setConfirmDeleteTeam(null)} className="p-1 hover:text-gray-700 text-gray-500">✕</button>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmDeleteTeam(team.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="text-center p-2 rounded-xl bg-[var(--bg-flat-50)]">
                  <p className="text-lg font-bold text-[var(--text-dark-h)]">{team.member_count || 0}</p>
                  <p className="text-[10px] text-[var(--text-muted)]">أعضاء</p>
                </div>
                <div className="text-center p-2 rounded-xl bg-[var(--bg-flat-50)]">
                  <p className="text-lg font-bold text-yellow-600 dark:text-yellow-400">{team.pending_count || 0}</p>
                  <p className="text-[10px] text-[var(--text-muted)]">بالانتظار</p>
                </div>
                <div className="text-center p-2 rounded-xl bg-[var(--bg-flat-50)]">
                  <p className="text-sm font-bold text-[var(--text-dark-h)]">{formatSeconds(team.monthly_target_seconds)}</p>
                  <p className="text-[10px] text-[var(--text-muted)]">الهدف</p>
                </div>
              </div>

              <button onClick={() => setShowMembers(team)}
                className="w-full py-2 text-sm text-primary bg-primary/5 hover:bg-primary/10 rounded-xl transition font-bold">
                <Users className="w-4 h-4 inline ml-1" /> إدارة الأعضاء
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreate || editTeam) && (
        <TeamFormModal
          team={editTeam}
          employees={employees}
          onClose={() => { setShowCreate(false); setEditTeam(null); }}
          onSaved={() => { setShowCreate(false); setEditTeam(null); load(); }}
        />
      )}

      {/* Members Modal */}
      {showMembers && (
        <MembersModal team={showMembers} user={user} isOwner={isOwner} isTeamLeader={isTeamLeader} employees={employees}
          onClose={() => setShowMembers(null)} onRefresh={load} />
      )}
    </div>
  );
}

function TeamFormModal({ team, employees, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: team?.name || '',
    leader_employee_id: team?.leader_employee_id || '',
    monthly_target_seconds: team?.monthly_target_seconds || 0,
    team_type: team?.team_type || (team?.leader_employee_id === 0 ? 'internal' : 'external'),
    leader_rate_per_second: team?.leader_rate_per_second || ''
  });
  const [saving, setSaving] = useState(false);
  const isInternal = form.team_type === 'internal';

  const handleSave = async () => {
    if (!form.name) return toast.error('الاسم مطلوب');
    if (!isInternal && !form.leader_employee_id) return toast.error('القائد مطلوب للفرق الخارجية');
    setSaving(true);
    try {
      const payload = { ...form };
      if (isInternal) payload.leader_employee_id = 0;
      if (team) await updateTmTeam(team.id, payload);
      else await createTmTeam(payload);
      toast.success(team ? 'تم التحديث' : 'تم الإنشاء');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={team ? 'تعديل فريق' : 'فريق جديد'} onClose={onClose}>
      <div className="space-y-4">
        {/* Team Type Toggle */}
        <div className="flex rounded-xl overflow-hidden border border-[var(--border-light)]">
          <button type="button" onClick={() => setForm(f => ({ ...f, team_type: 'external' }))}
            className={`flex-1 py-2.5 text-sm font-bold transition-all ${
              !isInternal ? 'bg-blue-600 text-white' : 'bg-white dark:bg-[#1a1b1e] text-[var(--text-gray)] hover:bg-blue-50 dark:hover:bg-blue-900/20'
            }`}>
            فريق خارجي (مستقلين)
          </button>
          <button type="button" onClick={() => setForm(f => ({ ...f, team_type: 'internal', leader_employee_id: 0 }))}
            className={`flex-1 py-2.5 text-sm font-bold transition-all ${
              isInternal ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-[#1a1b1e] text-[var(--text-gray)] hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
            }`}>
            فريق داخلي (بدون قائد)
          </button>
        </div>

        {isInternal && (
          <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
            <p className="text-xs text-emerald-700 dark:text-emerald-400 font-bold">فريق داخلي: يضم موظفي الوكالة الحاليين فقط، ولا يتطلب إنشاء حسابات جديدة للأعضاء.</p>
          </div>
        )}

        <LabeledInput label="اسم الفريق *">
          <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="مثال: فريق التصميم" className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow" />
        </LabeledInput>

        {!isInternal && (
          <>
            <div>
              <label className="block text-xs font-bold text-[var(--text-gray)] mb-1">قائد الفريق *</label>
              <select value={form.leader_employee_id || ''} onChange={e => setForm(f => ({ ...f, leader_employee_id: parseInt(e.target.value) }))}
                className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none">
                <option value="">اختر قائد</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <LabeledInput label="تسعيرة القائد (لكل ثانية د.ع)">
              <input type="text" inputMode="decimal" value={form.leader_rate_per_second} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setForm(f => ({ ...f, leader_rate_per_second: e.target.value }))} placeholder="مثال: 500 (اتركه فارغاً لاعتماد التسعيرة العامة)" className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow" />
            </LabeledInput>
          </>
        )}
        <LabeledInput label="الهدف الشهري (ثواني)">
          <input type="text" inputMode="decimal" value={form.monthly_target_seconds} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setForm(f => ({ ...f, monthly_target_seconds: parseInt(e.target.value) || 0 }))} placeholder="مثال: 3600" className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow" />
        </LabeledInput>
        <button onClick={handleSave} disabled={saving}
          className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all disabled:opacity-50">
          {saving ? 'جاري الحفظ...' : 'حفظ'}
        </button>
      </div>
    </Modal>
  );
}

function MembersModal({ team, user, isOwner, isTeamLeader, employees, onClose, onRefresh }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newMember, setNewMember] = useState({ employee_name: '', username: '', password: '', target_seconds: 0, existing_employee_id: '' });
  const isInternal = team.team_type === 'internal';
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('members');
  const [paySettings, setPaySettings] = useState([]);
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm] = useState({ amount_iqd: '', notes: '' });
  const [settingForm, setSettingForm] = useState(null);
  const [deductModal, setDeductModal] = useState(null);
  const [deductForm, setDeductForm] = useState({ deduction_type: 'manual', amount_iqd: '', reason: '' });

  const loadMembers = async () => {
    try {
      setLoading(true);
      setMembers(await getTmTeamMembers(team.id));
    } catch (e) {}
    finally { setLoading(false); }
  };

  const loadPaySettings = async () => {
    try { setPaySettings(await getTmPaymentSettings(team.id)); } catch {}
  };

  useEffect(() => { loadMembers(); }, []);
  useEffect(() => { if (tab === 'payments' && isTeamLeader) loadPaySettings(); }, [tab]);

  const handleAddMember = async () => {
    if (isInternal && !newMember.existing_employee_id) return toast.error('يرجى اختيار موظف');
    if (!isInternal && !newMember.employee_name) return toast.error('الاسم مطلوب');
    setSaving(true);
    try {
      await requestTmMember(team.id, newMember);
      toast.success(isInternal ? 'تمت إضافة العضو بنجاح' : 'تم إرسال طلب الإضافة');
      setShowAdd(false);
      setNewMember({ employee_name: '', username: '', password: '', target_seconds: 0, existing_employee_id: '' });
      loadMembers();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
    finally { setSaving(false); }
  };

  const [confirmRemoveMember, setConfirmRemoveMember] = useState(null);

  const handleApprove = async (mid) => {
    try { await approveTmMember(team.id, mid); toast.success('تمت الموافقة'); loadMembers(); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
  };

  const handleReject = async (mid) => {
    try { await rejectTmMember(team.id, mid); toast.success('تم الرفض'); loadMembers(); }
    catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
  };

  const handleRemove = async (mid, memberName, activeTasks) => {
    if (activeTasks > 0) {
      return toast.error(`لا يمكن إزالة "${memberName}" — لديه ${activeTasks} مهام نشطة. أنهِ المهام أولاً.`);
    }
    if (!window.confirm(`⚠️ هل أنت متأكد من إزالة "${memberName}" من الفريق نهائياً؟\n\nهذا الإجراء لا يمكن التراجع عنه.`)) return;
    try { await removeTmMember(team.id, mid); toast.success('تمت الإزالة'); loadMembers(); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
  };

  const handleSaveSetting = async () => {
    if (!settingForm) return;
    setSaving(true);
    try {
      await saveTmPaymentSetting(team.id, settingForm);
      toast.success('تم حفظ إعداد الصرف');
      setSettingForm(null);
      loadPaySettings();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
    finally { setSaving(false); }
  };

  const handlePay = async () => {
    if (!payModal || !payForm.amount_iqd) return toast.error('المبلغ مطلوب');
    setSaving(true);
    try {
      await createTmTeamPayment(team.id, {
        employee_id: payModal.employee_id,
        amount_iqd: parseFloat(payForm.amount_iqd),
        notes: payForm.notes
      });
      toast.success('تم صرف المبلغ بنجاح 💰');
      setPayModal(null);
      setPayForm({ amount_iqd: '', notes: '' });
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
    finally { setSaving(false); }
  };

  const handleDeduct = async () => {
    if (!deductModal || !deductForm.amount_iqd) return toast.error('المبلغ مطلوب');
    if (!deductForm.reason || deductForm.reason.trim().length < 3) return toast.error('يجب كتابة سبب الخصم');
    setSaving(true);
    try {
      await createTmDeduction(team.id, {
        employee_id: deductModal.employee_id,
        deduction_type: deductForm.deduction_type,
        amount_iqd: parseFloat(deductForm.amount_iqd),
        reason: deductForm.reason
      });
      toast.success('تم تسجيل الخصم ⛔');
      setDeductModal(null);
      setDeductForm({ deduction_type: 'manual', amount_iqd: '', reason: '' });
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
    finally { setSaving(false); }
  };

  const typeLabels = { per_second: 'بالثانية', fixed_salary: 'راتب ثابت', per_task: 'لكل مهمة', percentage: 'نسبة' };
  const deductionTypeLabels = { late: 'تأخير', quality: 'جودة ضعيفة', absence: 'غياب', manual: 'خصم يدوي' };
  const inputCls = "w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow";

  return (
    <Modal title={`أعضاء فريق: ${team.name}`} onClose={onClose}>
      <div className="space-y-4">
        {/* Tabs */}
        {isTeamLeader && (
          <div className="flex gap-2 border-b border-[var(--border-light)] pb-2">
            <button onClick={() => setTab('members')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${tab === 'members' ? 'bg-primary text-white' : 'text-[var(--text-gray)] hover:bg-[var(--bg-flat-50)]'}`}>
              <Users className="w-3.5 h-3.5 inline ml-1" /> الأعضاء
            </button>
            <button onClick={() => setTab('payments')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${tab === 'payments' ? 'bg-green-600 text-white' : 'text-[var(--text-gray)] hover:bg-[var(--bg-flat-50)]'}`}>
              💰 إعدادات الصرف
            </button>
          </div>
        )}

        {/* Members Tab */}
        {tab === 'members' && (
          <>
            {(isTeamLeader || isOwner) && (
              <button onClick={() => setShowAdd(!showAdd)}
                className="flex items-center gap-2 text-sm text-primary hover:text-primary-dark font-bold">
                <UserPlus className="w-4 h-4" /> إضافة عضو جديد
              </button>
            )}

        {showAdd && (
          <div className="p-4 rounded-xl border border-dashed border-primary/30 bg-primary/5 dark:bg-primary/10 space-y-3">
            {isInternal ? (
              <LabeledInput label="اختر موظفاً من الوكالة *">
                <select value={newMember.existing_employee_id} onChange={e => setNewMember(f => ({ ...f, existing_employee_id: e.target.value }))} className={inputCls}>
                  <option value="">-- اختر الموظف --</option>
                  {employees?.filter(emp => !members.find(m => m.employee_id === emp.id)).map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </LabeledInput>
            ) : (
              <>
                <LabeledInput label="اسم الموظف *">
                  <input type="text" value={newMember.employee_name} onChange={e => setNewMember(f => ({ ...f, employee_name: e.target.value }))} placeholder="أدخل اسم الموظف" className={inputCls} />
                </LabeledInput>
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput label="اسم المستخدم">
                    <input type="text" value={newMember.username} onChange={e => setNewMember(f => ({ ...f, username: e.target.value }))} placeholder="ahmed1" className={inputCls} />
                  </LabeledInput>
                  <LabeledInput label="كلمة المرور">
                    <input type="password" value={newMember.password} onChange={e => setNewMember(f => ({ ...f, password: e.target.value }))} placeholder="••••••" className={inputCls} />
                  </LabeledInput>
                </div>
              </>
            )}
            <LabeledInput label="الهدف الشهري (ثواني)">
              <input type="text" inputMode="decimal" value={newMember.target_seconds} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setNewMember(f => ({ ...f, target_seconds: e.target.value }))} placeholder="1800" className={inputCls} />
            </LabeledInput>
            <button onClick={handleAddMember} disabled={saving}
              className="w-full py-2 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold text-sm disabled:opacity-50">
              {saving ? 'جاري الإرسال...' : isInternal ? 'إضافة العضو' : 'إرسال طلب إضافة'}
            </button>
          </div>
        )}

        {loading ? (
          <p className="text-center text-sm text-[var(--text-muted)]">جاري التحميل...</p>
        ) : members.length === 0 ? (
          <p className="text-center text-sm text-[var(--text-muted)]">لا يوجد أعضاء</p>
        ) : (
          <div className="space-y-2">
            {members.map(m => (
              <div key={m.id} className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                m.status === 'pending' ? 'border-yellow-200 bg-yellow-50/50 dark:border-yellow-900/30 dark:bg-yellow-900/10'
                : 'border-[var(--border-light)]'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    m.status === 'active' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400'
                  }`}>
                    {m.employee_name?.charAt(0)}
                  </div>
                  <div>
                    <h4 className="text-sm font-bold m-0">{m.employee_name}</h4>
                    <p className="text-[10px] text-[var(--text-muted)] mt-1">{m.employee_role} {m.username ? `(@${m.username})` : ''}</p>
                    {m.status === 'pending' && <p className="text-[10px] text-yellow-600 dark:text-yellow-400 font-bold mt-1">بانتظار موافقة المالك...</p>}
                    <p className="text-[10px] text-primary mt-1 font-bold">التارجت: {formatSeconds(m.target_seconds)}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  {isOwner && m.status === 'pending' && (
                    <>
                      <button onClick={() => handleApprove(m.id)} className="p-1.5 rounded-lg bg-green-100 dark:bg-green-900/20 hover:bg-green-200 transition" title="موافقة">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </button>
                      <button onClick={() => handleReject(m.id)} className="p-1.5 rounded-lg bg-red-100 dark:bg-red-900/20 hover:bg-red-200 transition" title="رفض">
                        <XCircle className="w-4 h-4 text-red-500" />
                      </button>
                    </>
                  )}
                  {(isOwner || isTeamLeader) && m.status === 'active' && !m.is_leader && (
                    <div className="flex items-center gap-2">
                      {isTeamLeader && (
                        <label className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 px-2.5 py-1 rounded-xl cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/20 transition text-[10px] font-bold text-blue-700 dark:text-blue-400 select-none">
                          <input 
                            type="checkbox" 
                            checked={m.show_financials === 1 || m.show_financials === true || m.show_financials === '1'}
                            onChange={async (e) => {
                              const checked = e.target.checked;
                              try {
                                await toggleMemberFinancials(team.id, m.employee_id, checked);
                                toast.success(checked ? 'تم تفعيل رؤية المستحقات للعضو' : 'تم إخفاء المستحقات عن العضو');
                                loadMembers();
                              } catch (err) {
                                toast.error('خطأ في تعديل الصلاحية');
                              }
                            }}
                            className="w-3.5 h-3.5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                          />
                          💵 عرض الأرباح
                        </label>
                      )}
                      <button onClick={() => handleRemove(m.id, m.employee_name, m.active_tasks_count || 0)}
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition" title="إزالة العضو">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}

        {/* Payment Settings Tab (TL only) */}
        {tab === 'payments' && isTeamLeader && (
          <div className="space-y-3">
            {paySettings.length === 0 ? (
              <p className="text-center text-sm text-[var(--text-muted)]">لا يوجد أعضاء نشطين</p>
            ) : paySettings.map(ps => (
              <div key={ps.employee_id} className="p-3 rounded-xl border border-[var(--border-light)] space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-xs font-bold text-green-600">{ps.employee_name?.charAt(0)}</div>
                    <span className="text-sm font-bold text-[var(--text-dark-h)]">{ps.employee_name}</span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setSettingForm({ employee_id: ps.employee_id, payment_type: ps.payment_type || 'per_second', rate_value: ps.rate_value || 250 })}
                      className="px-2 py-1 text-[10px] font-bold rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 hover:bg-blue-100 transition">
                      <Edit3 className="w-3 h-3 inline ml-0.5" /> إعداد
                    </button>
                    <button onClick={() => setPayModal(ps)}
                      className="px-2 py-1 text-[10px] font-bold rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 hover:bg-green-100 transition">
                      💰 صرف
                    </button>
                    <button onClick={() => setDeductModal(ps)}
                      className="px-2 py-1 text-[10px] font-bold rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-100 transition">
                      ⛔ خصم
                    </button>
                  </div>
                </div>
                {ps.payment_type ? (
                  <p className="text-[10px] text-[var(--text-muted)]">
                    النوع: <span className="font-bold text-primary">{typeLabels[ps.payment_type]}</span> — القيمة: <span className="font-bold">{ps.rate_value}</span> {ps.payment_type === 'percentage' ? '%' : 'د.ع'}
                  </p>
                ) : (
                  <p className="text-[10px] text-yellow-600 font-bold">⚠️ لم يتم تحديد إعداد صرف</p>
                )}
              </div>
            ))}

            {/* Setting Form Inline */}
            {settingForm && (
              <div className="p-4 rounded-xl border-2 border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 space-y-3">
                <h4 className="text-sm font-bold text-[var(--text-dark-h)]">إعداد الصرف</h4>
                <select value={settingForm.payment_type} onChange={e => setSettingForm(f => ({ ...f, payment_type: e.target.value }))} className={inputCls}>
                  <option value="per_second">بالثانية</option>
                  <option value="fixed_salary">راتب ثابت</option>
                  <option value="per_task">لكل مهمة</option>
                  <option value="percentage">نسبة (0-1)</option>
                </select>
                <input type="text" inputMode="decimal" value={settingForm.rate_value}
                  onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }}
                  onChange={e => setSettingForm(f => ({ ...f, rate_value: parseFloat(e.target.value) || 0 }))}
                  placeholder={settingForm.payment_type === 'percentage' ? '0.50' : '250'} className={inputCls} />
                <div className="flex gap-2">
                  <button onClick={handleSaveSetting} disabled={saving} className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm disabled:opacity-50">{saving ? '...' : 'حفظ'}</button>
                  <button onClick={() => setSettingForm(null)} className="px-4 py-2 text-sm rounded-xl border border-[var(--border-light)] text-[var(--text-gray)]">إلغاء</button>
                </div>
              </div>
            )}

            {/* Pay Modal Inline */}
            {payModal && (
              <div className="p-4 rounded-xl border-2 border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10 space-y-3">
                <h4 className="text-sm font-bold text-[var(--text-dark-h)]">💰 صرف لـ {payModal.employee_name}</h4>
                <input type="text" inputMode="decimal" value={payForm.amount_iqd} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setPayForm(f => ({ ...f, amount_iqd: e.target.value }))} placeholder="المبلغ (د.ع)" className={inputCls} />
                <input type="text" value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} placeholder="ملاحظات (اختياري)" className={inputCls} />
                <div className="flex gap-2">
                  <button onClick={handlePay} disabled={saving} className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-sm disabled:opacity-50">{saving ? '...' : 'تأكيد الصرف'}</button>
                  <button onClick={() => { setPayModal(null); setPayForm({ amount_iqd: '', notes: '' }); }} className="px-4 py-2 text-sm rounded-xl border border-[var(--border-light)] text-[var(--text-gray)]">إلغاء</button>
                </div>
              </div>
            )}

            {/* Deduction Form Inline */}
            {deductModal && (
              <div className="p-4 rounded-xl border-2 border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10 space-y-3">
                <h4 className="text-sm font-bold text-[var(--text-dark-h)]">⛔ خصم على {deductModal.employee_name}</h4>
                <select value={deductForm.deduction_type} onChange={e => setDeductForm(f => ({ ...f, deduction_type: e.target.value }))} className={inputCls}>
                  <option value="manual">خصم يدوي</option>
                  <option value="late">تأخير</option>
                  <option value="quality">جودة ضعيفة</option>
                  <option value="absence">غياب</option>
                </select>
                <input type="text" inputMode="decimal" value={deductForm.amount_iqd} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setDeductForm(f => ({ ...f, amount_iqd: e.target.value }))} placeholder="مبلغ الخصم (د.ع)" className={inputCls} />
                <input type="text" value={deductForm.reason} onChange={e => setDeductForm(f => ({ ...f, reason: e.target.value }))} placeholder="سبب الخصم (مطلوب) *" className={inputCls} />
                <div className="flex gap-2">
                  <button onClick={handleDeduct} disabled={saving} className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-sm disabled:opacity-50">{saving ? '...' : 'تأكيد الخصم'}</button>
                  <button onClick={() => { setDeductModal(null); setDeductForm({ deduction_type: 'manual', amount_iqd: '', reason: '' }); }} className="px-4 py-2 text-sm rounded-xl border border-[var(--border-light)] text-[var(--text-gray)]">إلغاء</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

